// REACT
import { useEffect, useRef, useState, useCallback } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";

// NOTABENE SDK
import Notabene, {
  componentResponseToTxRequests,
  getRefreshResult,
  PersonType,
} from "@notabene/javascript-sdk";

// AGENT SECTIONS — extracted so the policy is unit-testable without booting
// the SDK. See notabeneAgentSections.ts for the per-kind rationale.
import {
  AGENT_SECTIONS,
  type NotabeneHostedMountKind,
} from "src/components/wallets/dropdown/notabeneAgentSections";

// WIDGET ASSET — why the SDK gets a CAIP-19 rather than our ticker. Pure
// module so notabeneWidgetAsset.test.ts pins the rule without the SDK.
import {
  isCaip19Like,
  resolveWidgetAsset,
} from "src/components/wallets/dropdown/notabeneWidgetAsset";

// API
import {
  useGetNotabeneAssetCaip19Query,
  useGetNotabeneTokenQuery,
} from "src/store/features/api/notabene";

// USER
import { useUserSlice } from "src/store/features/user.slice";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";
import { getSessionItem } from "src/utils/SessionManagement";

/**
 * Generic SafeConnect mount used by every Travel Rule flow that needs a
 * Notabene widget. The kind maps to a real SDK factory in
 * `@notabene/javascript-sdk@2.16.0`:
 *
 *   - withdrawal-assist  → `notabene.createWithdrawalAssist({...})`
 *                          Outbound transfer: PII collection + counterparty
 *                          resolution + ownership proof, all in one widget.
 *                          Used by D9 (CryptoWithdrawalNotabene) and D18
 *                          info-request recovery (re-mount with same txId).
 *   - connect-wallet     → `notabene.createConnectWallet({ asset })`
 *                          Standalone wallet ownership verification. Opens
 *                          a modal; resolves with `{ proof, txCreate }`.
 *                          Used by D3 self-hosted address-add (decoupled
 *                          from any specific transfer).
 *   - deposit-assist     → `notabene.createDepositAssist({...})`
 *                          Customer-side info collection for an INCOMPLETE
 *                          inbound deposit. Used by D13 (Workstream C).
 *
 * Earlier kinds (`ownership-attestation`, `info-request`, `vasp-picker`)
 * were removed — Notabene's SDK does not expose those as standalone
 * factories. Their workflows are subsumed:
 *   - ownership-attestation → either `connect-wallet` (D3) or the proof
 *     step inside `withdrawal-assist` at withdrawal time.
 *   - info-request → re-mount `withdrawal-assist` with the same txId; the
 *     SDK detects the existing transfer and surfaces missing fields.
 *   - vasp-picker → embedded inside `withdrawal-assist`'s counterparty
 *     step. For preview UX at address-add time, callers use
 *     `useGetVaspDirectoryQuery` directly (backend-proxied search).
 */
export type { NotabeneHostedMountKind };

export interface NotabeneHostedMountResult {
  /** Raw SDK completion payload (preserved verbatim for callers that need it). */
  raw: any;
  /** Transfer/wallet reference issued by SafeConnect. */
  reference?: string;
  /** Stable wallet attestation reference (`connect-wallet` proof.id). */
  walletVerificationReference?: string;
  /** Resolved counterparty VASP info if present in the SDK response. */
  vaspDid?: string;
  vaspName?: string;
  /** Pre-built IVMS101 payload ready for `/append`. */
  ivms101?: Record<string, unknown>;
  /** Optional confirmRelationship blob from SafeConnect. */
  confirmRelationship?: Record<string, unknown>;
}

export interface NotabeneHostedMountProps {
  kind: NotabeneHostedMountKind;
  /** Notabene transfer id — required for info-request re-mount + deposit-assist. */
  txId?: string;
  /**
   * Asset — required for all kinds. Accepts our ticker (`ETH`, `W`) or a
   * CAIP-19. A ticker is resolved to its CAIP-19 via
   * `/notabene/asset/:tokenCode/caip19` before the SDK mounts: the hosted
   * widget rejects one-character tickers and silently hides its Complete
   * button (see notabeneWidgetAsset.ts). Falls back to the ticker on a miss.
   */
  asset?: string;
  /** Outbound destination address (`withdrawal-assist`). */
  destinationAddress?: string;
  /** Inbound source address (`deposit-assist`). */
  sourceAddress?: string;
  /** Amount as a number (`withdrawal-assist`, `deposit-assist`). */
  amount?: number;
  /**
   * Enable Counterparty Assist on `withdrawal-assist` / `deposit-assist`.
   * When on, SafeConnect lets the customer share a link with a counterparty
   * (or another device) to fill in their own data; the widget emits a
   * `refreshSource` we then async-fetch + decrypt + `/append`.
   *
   *   - `false` / undefined: feature off (default)
   *   - `true`: enable for all three person types (SELF, NATURAL, LEGAL)
   *   - explicit array: only the listed types
   */
  counterpartyAssist?: boolean | Array<"self" | "natural" | "legal">;
  /**
   * The customer's display name, forwarded as `value.customer.name`. The
   * widget's "Myself" sender/recipient option builds its IVMS person by
   * copying `value.customer` — when absent it silently emits an EMPTY person
   * that Notabene's /append later rejects as "Invalid IVMS101 data".
   */
  customerName?: string;
  /** Called once SafeConnect's `complete` resolves with `valid: true`. */
  onComplete: (data: NotabeneHostedMountResult) => void;
  /** Called when the user explicitly cancels (closes the SDK / clicks back). */
  onCancel?: () => void;
  /**
   * Optional hosted-page URL for the redirect fallback when the SDK can't
   * load (CSP block, browser extension interference, ad blocker). When
   * supplied, the fallback button opens this URL in a new tab; the host
   * page is responsible for callback handling via `?notabene_callback=1`.
   * Defaults to `null`, which hides the fallback button.
   */
  redirectFallbackUrl?: string | null;
}

type ComponentState = "loading-token" | "ready" | "error";

const FIFTEEN_SECONDS = 15_000;

const resolveCounterpartyTypes = (
  config: NotabeneHostedMountProps["counterpartyAssist"],
): PersonType[] | undefined => {
  if (!config) return undefined;
  if (config === true) return [PersonType.SELF, PersonType.NATURAL, PersonType.LEGAL];
  return config.map((v) => {
    switch (v) {
      case "self":
        return PersonType.SELF;
      case "natural":
        return PersonType.NATURAL;
      case "legal":
        return PersonType.LEGAL;
    }
  });
};

const NotabeneHostedMount: React.FC<NotabeneHostedMountProps> = ({
  kind,
  txId,
  asset,
  destinationAddress,
  sourceAddress,
  amount,
  counterpartyAssist,
  customerName,
  onComplete,
  onCancel,
  redirectFallbackUrl = null,
}) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const sdkRef = useRef<any>(null);
  const [state, setState] = useState<ComponentState>("loading-token");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [showRedirectFallback, setShowRedirectFallback] = useState(false);
  const currentEmail = getSessionItem("email");
  // Fallback for call sites that don't pass an explicit customerName — the
  // logged-in user's profile name covers the common "Myself" case.
  const { details: userDetails } = useUserSlice();
  const resolvedCustomerName = customerName || userDetails?.name || undefined;

  const { data: tokenData, isLoading: isTokenLoading, error: tokenError } =
    useGetNotabeneTokenQuery();

  // Resolve the ticker to a CAIP-19 before mounting — the widget validates
  // the raw `value.asset` and a one-character ticker (`W`) fails it. A
  // CAIP-19 input skips the lookup; a 404/error falls back to the ticker.
  const needsAssetLookup = !!asset && !isCaip19Like(asset);
  const assetLookup = useGetNotabeneAssetCaip19Query(asset ?? "", { skip: !needsAssetLookup });
  const { asset: widgetAsset, pending: widgetAssetPending } = resolveWidgetAsset(asset, {
    settled: !needsAssetLookup || assetLookup.isSuccess || assetLookup.isError,
    caip19: assetLookup.data?.caip19 ?? null,
  });

  // CSP / ad-blocker recovery: surface the redirect-fallback CTA if the SDK
  // has been mounting for >15s. Only relevant for inline-mounted kinds —
  // `connect-wallet` opens a modal so a stuck mount is not a thing there.
  useEffect(() => {
    if (state !== "ready" || sdkReady) return;
    if (!redirectFallbackUrl) return;
    if (kind === "connect-wallet") return;
    const t = setTimeout(() => setShowRedirectFallback(true), FIFTEEN_SECONDS);
    return () => clearTimeout(t);
  }, [state, sdkReady, redirectFallbackUrl, kind]);

  const trackKindEvent = useCallback(
    (event: string, extra: Record<string, any> = {}) =>
      trackEvent(
        event,
        { kind, txId, asset, widgetAsset, email: currentEmail, ...extra },
        "Notabene Hosted Mount",
      ),
    [kind, txId, asset, widgetAsset, currentEmail],
  );

  useEffect(() => {
    if (tokenError) {
      setState("error");
      setErrorMessage("Unable to connect to travel rule service.");
    } else if (tokenData?.token) {
      setState("ready");
    }
  }, [tokenData?.token, tokenError]);

  /**
   * Convert any SafeConnect completion payload (regular or via refreshSource)
   * into our shared `NotabeneHostedMountResult` shape and forward it to the
   * caller. Centralised so all three kinds emit the same shape.
   */
  const emitCompletion = useCallback(
    (result: any) => {
      let requests: any = null;
      try {
        requests = componentResponseToTxRequests(result, tokenData?.token || "");
      } catch {
        requests = null;
      }
      const ivms101Inner =
        (requests?.ivms101 as any)?.ivms101 ?? (requests?.ivms101 as any);
      const txCreate = result?.txCreate ?? {};
      // Notabene's `OwnershipProof` (and its SignatureProof subtype) does NOT
      // expose an `id` field — only `did`, `address`, `type`, `status` (plus
      // SignatureProof's `proof`/`attestation`/`wallet_provider`). The
      // `requestID` UUID on the SDK's `ComponentResponse` is the stable,
      // always-present identifier we should hand to the backend as the
      // attestation reference; `proof.did` is a useful secondary signal that
      // ties the reference to a specific wallet binding.
      const stableProofRef =
        result?.proof?.id ||
        result?.walletVerificationReference ||
        result?.attestation?.reference ||
        (result?.proof?.did
          ? result?.proof?.address
            ? `${result.proof.did}#${result.proof.address}`
            : result.proof.did
          : undefined) ||
        result?.requestID ||
        txCreate.id ||
        result?.id;
      const summary: NotabeneHostedMountResult = {
        raw: result,
        reference: txCreate.id || result?.reference || result?.id || result?.requestID,
        walletVerificationReference: stableProofRef,
        vaspDid: result?.counterparty?.did || result?.vaspDid,
        vaspName: result?.counterparty?.name || result?.vaspName,
        ivms101: ivms101Inner as Record<string, unknown> | undefined,
        confirmRelationship:
          requests?.confirmRelationship as Record<string, unknown> | undefined,
      };
      onComplete(summary);
    },
    [onComplete, tokenData?.token],
  );

  // Mount the SDK once we have a token + DOM node + (for kinds that need
  // them) the contextual params. Re-mounting is destructive — guarded by
  // `sdkRef.current` so dep churn doesn't wipe the user's in-progress
  // input.
  useEffect(() => {
    if (sdkRef.current) return;
    if (state !== "ready") return;
    if (!tokenData?.token) return;
    // Ticker → CAIP-19 lookup still in flight; mounting now would hand the
    // widget the raw ticker and re-create the hidden-Submit bug.
    if (widgetAssetPending) return;
    // connect-wallet uses openModal(), no DOM target needed.
    if (kind !== "connect-wallet" && !mountRef.current) return;

    const mountId = `notabene-hosted-${kind}`;
    if (kind !== "connect-wallet" && mountRef.current) mountRef.current.id = mountId;

    let widget: any;
    try {
      const notabene = new Notabene({
        nodeUrl: tokenData.nodeUrl,
        authToken: tokenData.token,
        theme: { mode: "light", primaryColor: "#0a852d", fontFamily: "Söhne" },
        locale: "en",
      });

      const counterpartyTypes = resolveCounterpartyTypes(counterpartyAssist);
      const sdkOptions = {
        agentSections: AGENT_SECTIONS[kind],
        ...(counterpartyTypes
          ? { counterpartyAssist: { counterpartyTypes } }
          : {}),
      };

      switch (kind) {
        case "withdrawal-assist": {
          if (!widgetAsset || !destinationAddress || !amount) {
            throw new Error(
              "withdrawal-assist requires asset, destinationAddress, amount",
            );
          }
          widget = notabene.createWithdrawalAssist(
            {
              asset: widgetAsset,
              destination: destinationAddress,
              amountDecimal: amount,
              // Feeds the widget's "Myself" path — without a customer it
              // emits an empty IVMS person that /append rejects.
              ...(resolvedCustomerName ? { customer: { name: resolvedCustomerName } } : {}),
              ...(txId ? { transactionId: txId } : {}),
            },
            sdkOptions,
          );
          break;
        }
        case "deposit-assist": {
          // D13 customer-side info collection for INCOMPLETE inbound
          // deposits. Workstream C drives this.
          if (!widgetAsset || !sourceAddress) {
            throw new Error("deposit-assist requires asset, sourceAddress");
          }
          widget = notabene.createDepositAssist(
            {
              asset: widgetAsset,
              source: sourceAddress,
              ...(amount !== undefined ? { amountDecimal: amount } : {}),
              // Feeds the widget's "Myself" path — without a customer it
              // emits an empty IVMS person that /append rejects.
              ...(resolvedCustomerName ? { customer: { name: resolvedCustomerName } } : {}),
              ...(txId ? { transactionId: txId } : {}),
            },
            sdkOptions,
          );
          break;
        }
        case "connect-wallet": {
          if (!widgetAsset) throw new Error("connect-wallet requires asset");
          widget = notabene.createConnectWallet(
            { asset: widgetAsset },
            { agentSections: AGENT_SECTIONS["connect-wallet"] },
          );
          // openModal returns a promise that resolves once the user
          // completes the verification (or rejects on cancel/error).
          widget
            .openModal()
            .then((result: any) => {
              setSdkReady(true);
              trackKindEvent("Notabene Hosted Mount Completed");
              emitCompletion(result);
            })
            .catch((err: any) => {
              const msg = err?.message || "Wallet verification was cancelled or failed.";
              trackKindEvent("Notabene Hosted Mount Modal Error", { error: msg });
              if (onCancel && /cancel/i.test(msg)) {
                onCancel();
              } else {
                setState("error");
                setErrorMessage(msg);
              }
            });
          sdkRef.current = widget;
          return;
        }
        default: {
          throw new Error(`Unknown NotabeneHostedMount kind: ${kind}`);
        }
      }

      // Inline-mount path for withdrawal-assist + deposit-assist.
      widget.mount(`#${mountId}` as any);
      sdkRef.current = widget;

      widget.on?.("ready", () => {
        setSdkReady(true);
        trackKindEvent("Notabene Hosted Mount Ready");
      });
      widget.on?.("error", (err: any) => {
        const msg = err?.message || "An error occurred with the travel rule widget.";
        trackKindEvent("Notabene Hosted Mount Inline Error", { error: msg });
      });

      widget
        .completion?.()
        .then(async (result: any) => {
          const valid = result?.valid !== false;
          if (!valid) {
            trackKindEvent("Notabene Hosted Mount Validation Failed");
            setErrorMessage("Travel rule verification failed. Please try again.");
            return;
          }
          // If the widget emits a refreshSource (Counterparty Assist
          // shared-link case), the local response is partial — fetch +
          // decrypt the actual data from Notabene's safe-connections
          // endpoint via the SDK's `getRefreshResult` helper.
          if (result?.refreshSource) {
            trackKindEvent("Notabene Hosted Mount Refresh Source Received");
            try {
              const refreshed = await getRefreshResult(result.refreshSource);
              emitCompletion(refreshed);
              return;
            } catch (err: any) {
              const msg =
                err?.message || "Failed to retrieve counterparty data. Please try again.";
              trackKindEvent("Notabene Hosted Mount Refresh Source Error", { error: msg });
              setState("error");
              setErrorMessage(msg);
              return;
            }
          }
          trackKindEvent("Notabene Hosted Mount Completed");
          emitCompletion(result);
        })
        .catch((err: any) => {
          const msg = err?.message || "Failed to complete travel rule verification.";
          trackKindEvent("Notabene Hosted Mount Completion Error", { error: msg });
          setState("error");
          setErrorMessage(msg);
        });
    } catch (err: any) {
      const msg = err?.message || "Failed to load travel rule widget.";
      trackKindEvent("Notabene Hosted Mount Init Error", { error: msg });
      setState("error");
      setErrorMessage(msg);
      // Surface the redirect fallback immediately on init failures so users
      // can recover even when the iframe never opens at all.
      if (redirectFallbackUrl && kind !== "connect-wallet") {
        setShowRedirectFallback(true);
      }
    }
  }, [
    state,
    tokenData?.token,
    tokenData?.nodeUrl,
    kind,
    txId,
    widgetAsset,
    widgetAssetPending,
    destinationAddress,
    sourceAddress,
    amount,
    counterpartyAssist,
    resolvedCustomerName,
    redirectFallbackUrl,
    emitCompletion,
    onCancel,
    trackKindEvent,
  ]);

  // Tear down on unmount — separate effect so dep changes don't wipe the
  // SDK instance.
  useEffect(
    () => () => {
      const sdk = sdkRef.current;
      if (!sdk) return;
      try {
        if (typeof sdk.unmount === "function") sdk.unmount();
        else if (typeof sdk.destroy === "function") sdk.destroy();
        else if (typeof sdk.closeModal === "function") sdk.closeModal();
      } catch {
        /* already torn down */
      }
      sdkRef.current = null;
    },
    [],
  );

  if (isTokenLoading || state === "loading-token") {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={6}>
        <CircularProgress size={36} />
        <Typography sx={{ mt: 2, color: "#667085" }}>
          Connecting to travel rule service…
        </Typography>
      </Box>
    );
  }

  if (state === "error") {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={6}
        px={2}
        sx={{
          backgroundColor: "#FEF3F2",
          borderRadius: "8px",
          border: "1px solid #FEE4E2",
        }}>
        <Typography
          sx={{ fontSize: 16, fontWeight: 600, color: "#B42318", mb: 1, textAlign: "center" }}>
          Travel rule widget unavailable
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#7A271A", textAlign: "center", mb: 2 }}>
          {errorMessage || "We couldn't load the travel rule widget."}
        </Typography>
        {redirectFallbackUrl && kind !== "connect-wallet" && (
          <Button
            variant="outlined"
            onClick={() => window.open(redirectFallbackUrl, "_blank", "noopener")}
            data-testid="notabene-hosted-mount-redirect-fallback">
            Open hosted page
          </Button>
        )}
        {onCancel && (
          <Button onClick={onCancel} sx={{ mt: 1 }}>
            Cancel
          </Button>
        )}
      </Box>
    );
  }

  // connect-wallet renders a small "verifying" indicator while the modal is
  // open — the modal owns the actual UI surface, this component just keeps
  // a placeholder in the page so the consumer's layout doesn't jump.
  if (kind === "connect-wallet") {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={4}>
        {!sdkReady && (
          <>
            <CircularProgress size={28} />
            <Typography sx={{ mt: 1.5, color: "#667085", fontSize: 13 }}>
              Verifying wallet…
            </Typography>
          </>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%", minHeight: 400 }}>
      {!sdkReady && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}>
          <CircularProgress size={32} />
          <Typography sx={{ ml: 2, color: "#667085" }}>
            Loading travel rule widget…
          </Typography>
        </Box>
      )}
      <Box
        ref={mountRef}
        data-testid={`notabene-hosted-mount-${kind}`}
        sx={{
          width: "100%",
          opacity: sdkReady ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
      {showRedirectFallback && redirectFallbackUrl && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Button
            variant="outlined"
            onClick={() => window.open(redirectFallbackUrl, "_blank", "noopener")}
            data-testid="notabene-hosted-mount-redirect-fallback">
            Open in hosted page
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default NotabeneHostedMount;
